import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import { ColorScheme } from 'mini-color/lib/schema/ColorScheme';
import { getColorSchemeFromPrimaryColor } from 'mini-color/lib/utils/colorSchemeGenerator';
import {
    defaultPrimaryColors,
    getOrderedDefaultPrimaryColors,
} from 'mini-color/lib/utils/defaultPrimaryColors';

interface Calendar {
    id: { id: string };
    hexColorString?: string;
    calendarColor?: number;
}

// Cache to map a calendar to it's assigned color
const calendarColorCache: Record<string, string> = {};

/**
 * Creates cache key using values that are used in determining the color to be assigned
 * @param calendar
 */
const createCacheKey = (calendar: Calendar): string =>
    `${calendar.id.id}_${calendar.hexColorString}_${calendar.calendarColor}`;

// All available colors to be used for calendars.
let availableCalendarColors: string[] = getOrderedDefaultPrimaryColors();

export function refreshAvailableCalendarColors(): string[] {
    availableCalendarColors = getOrderedDefaultPrimaryColors();
    return availableCalendarColors;
}

/**
 * The available colors to be used in calendar as per the order.
 */
function getAvailableCalendarColors(): string[] {
    return availableCalendarColors.length
        ? availableCalendarColors
        : refreshAvailableCalendarColors();
}

/**
 * Returns the calendar color scheme for a given calendar entry.
 * @param calendar
 */
export function getColorSchemeForCalendar(
    calendar: Calendar,
    isDarkTheme?: boolean
): ColorScheme | null {
    const cacheKey = createCacheKey(calendar);
    let calendarColor = calendarColorCache[cacheKey];
    if (!calendarColor) {
        calendarColor = assignCalendarColor(calendar);
        calendarColorCache[cacheKey] = calendarColor;
    }
    return getColorSchemeFromPrimaryColor(calendarColor, isDarkTheme);
}

/**
 * Returns the color scheme for a given calendar
 * @param calendar
 */
function assignCalendarColor(calendar: Calendar): string {
    let assignedColor;
    if (
        calendar?.hexColorString &&
        defaultPrimaryColors.filter(
            color => color.toLowerCase() === calendar.hexColorString.toLowerCase()
        ).length > 0
    ) {
        assignedColor = calendar.hexColorString;
    } else {
        assignedColor = getColorForCalendarColorIndex(calendar.calendarColor);
    }

    return assignedColor;
}

/**
 * Returns the color for a given calendar color index
 * @param calendarColorIndex
 */
function getColorForCalendarColorIndex(calendarColorIndex: CalendarColor): string {
    let assignedColor = null;
    const availableCalendarColors = getAvailableCalendarColors();
    if (calendarColorIndex == undefined || calendarColorIndex === CalendarColor.Auto) {
        // If Calendar color is undefined or auto, just pop it from the available calendar colors.
        assignedColor = availableCalendarColors.pop();
    } else {
        assignedColor = defaultPrimaryColors[calendarColorIndex];

        // Remove the color from the available colors
        const index = availableCalendarColors.indexOf(assignedColor);
        index > -1 && availableCalendarColors.splice(index, 1);
    }

    return assignedColor;
}
